import { useEffect, useState } from "react";
import { FileText, Mic2, Video, FolderOpen, ArrowUpRight, Play, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";

const CATEGORIES = [
  { key: "tutti", label: "Tutti", testid: "media-tab-all" },
  { key: "video", label: "Video", testid: "media-tab-video" },
  { key: "interviste", label: "Interviste", testid: "media-tab-interviews" },
  { key: "documenti", label: "Documenti", testid: "media-tab-documents" },
  { key: "altro", label: "Altro", testid: "media-tab-other" },
];

const ICONS = {
  video: Video,
  interviste: Mic2,
  documenti: FileText,
  altro: FolderOpen,
};

const VIDEOS = [
  {
    titolo: "Premio Manfred Bootz 2026 il riconoscimento al Professor Traverso per il contributo alla cura del glaucoma",
    descrizione: "Il Professor Carlo Enrico Traverso racconta il significato di un importante riconoscimento internazionale legato alla sua attività nel campo dell'Oftalmologia e del glaucoma.",
    src: "/videos/video-01.mp4",
    thumb: "/videos/thumb-01.jpg",
    testid: "media-card-video-1",
  },
  {
    titolo: "L'importanza della formazione e le linee guida per il trattamento del Glaucoma — Professor Carlo Enrico Traverso",
    descrizione: "Il Professor Carlo Enrico Traverso ripercorre alcune delle principali tappe della propria formazione e dell'esperienza professionale maturata in Italia e presso importanti centri oftalmologici internazionali.",
    src: "/videos/video-02.mp4",
    thumb: "/videos/thumb-02.jpg",
    testid: "media-card-video-2",
  },
  {
    titolo: "Glaucoma quanto è diffuso e perché è importante diagnosticarlo presto — ce ne parla il Professor Carlo Enrico Traverso",
    descrizione: "Il Professor Carlo Enrico Traverso affronta il tema del glaucoma, della sua diffusione e dell'importanza della diagnosi precoce e di un corretto percorso di cura.",
    src: "/videos/video-03.mp4",
    thumb: "/videos/thumb-03.jpg",
    testid: "media-card-video-3",
  },
  {
    titolo: "Come cambiano diagnosi e cura dei difetti visivi con le nuove tecnologie — Professor Carlo Enrico Traverso",
    descrizione: "Il Professor Carlo Enrico Traverso spiega il ruolo della prevenzione e dei controlli oculistici nel preservare la funzione visiva e la qualità di vita.",
    src: "/videos/video-04.mp4",
    thumb: "/videos/thumb-04.jpg",
    testid: "media-card-video-4",
  },
  {
    titolo: "La Cataratta e la qualità di vita: l'importanza della prevenzione con il Professor Carlo Enrico Traverso",
    descrizione: "Il Professor Carlo Enrico Traverso approfondisce il ruolo dell'evoluzione tecnologica nella valutazione e nel trattamento dei principali difetti visivi.",
    src: "/videos/video-05.mp4",
    thumb: "/videos/thumb-05.jpg",
    testid: "media-card-video-5",
  },
  {
    titolo: "Le malattie degenerative e la motivazione personale del Professor Carlo Enrico Traverso",
    descrizione: "Il Professor Carlo Enrico Traverso approfondisce il tema della cataratta e il ruolo dell'intervento chirurgico nel recupero della funzione visiva e della qualità di vita.",
    src: "/videos/video-06.mp4",
    thumb: "/videos/thumb-06.jpg",
    testid: "media-card-video-6",
  },
];

const ITEMS = [
  {
    categoria: "interviste",
    categoriaLabel: "Interviste",
    titolo: "Mai sottovalutare l'intervento di cataratta",
    fonte: "Eye Doctor",
    url: "https://www.eyedoctor.it/interviste/carlo-enrico-traverso/",
    azione: "Leggi",
    thumb: "/images/media-cataratta.jpg",
    testid: "media-card-interview-eyedoctor",
  },
  {
    categoria: "interviste",
    categoriaLabel: "Interviste",
    titolo: "Giornata Mondiale della Vista – Intervista al Professor Carlo Enrico Traverso",
    fonte: "Casa della Salute",
    url: "https://cds.it/news/giornata-mondiale-vista-intervista-professor-carlo-enrico-traverso/",
    azione: "Leggi",
    thumb: "/images/media-giornata-vista.jpg",
    testid: "media-card-interview-cds",
  },
  {
    categoria: "documenti",
    categoriaLabel: "Documenti",
    titolo: "Curriculum Vitae – Professor Carlo Enrico Traverso",
    fonte: "Università degli Studi di Genova",
    url: "https://rubrica.unige.it/static/cv/UEVGUlk=_cv_en.pdf",
    azione: "Apri documento",
    thumb: "/images/media-cv.jpg",
    testid: "media-card-cv",
  },
];

export default function Media() {
  const [active, setActive] = useState("tutti");
  const [current, setCurrent] = useState(null);

  const showVideos = active === "tutti" || active === "video";
  const filteredItems = active === "tutti" ? ITEMS : ITEMS.filter((i) => i.categoria === active);
  const isEmpty = !showVideos && filteredItems.length === 0;

  useEffect(() => {
    if (!current) return;
    const onKey = (e) => e.key === "Escape" && setCurrent(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [current]);

  return (
    <div data-testid="media-page">
      <PageHero
        testid="media-section"
        eyebrow="Contenuti"
        title="Media"
        subtitle="Interviste, approfondimenti e contenuti dedicati alla salute degli occhi e all'evoluzione dell'oftalmologia."
      />

      <section className="bg-[#F8FAF8]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div data-testid="media-tabs" className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                data-testid={c.testid}
                onClick={() => setActive(c.key)}
                className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-all duration-300 ${
                  active === c.key
                    ? "bg-[#0D3327] text-white"
                    : "border border-[#D5E2DB] bg-white text-[#4B5852] hover:border-[#2E6B50]/60 hover:text-[#0D3327]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {showVideos &&
              VIDEOS.map((v, i) => (
                <Reveal key={v.testid} delay={i * 0.06}>
                  <article
                    data-testid={v.testid}
                    className="group flex h-full flex-col overflow-hidden rounded-lg border border-[#D5E2DB] bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <button
                      type="button"
                      data-testid={`${v.testid}-play`}
                      onClick={() => setCurrent(v)}
                      aria-label={`Guarda il video: ${v.titolo}`}
                      className="relative block w-full cursor-pointer"
                    >
                      <img src={v.thumb} alt={v.titolo} loading="lazy" className="aspect-video w-full object-cover" />
                      <span className="absolute inset-0 bg-[#0D3327]/0 transition-colors duration-300 group-hover:bg-[#0D3327]/15" />
                      <span className="absolute left-3 top-3 rounded-full bg-[#0D3327]/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white">
                        Video
                      </span>
                      <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#0D3327]/85 text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                        <Play size={20} className="ml-0.5" fill="currentColor" />
                      </span>
                    </button>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="line-clamp-3 font-serif text-lg font-semibold leading-snug text-[#0D3327]">
                        {v.titolo}
                      </h3>
                      <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-[#4B5852]">
                        {v.descrizione}
                      </p>
                      <p className="mt-3 flex-1 text-[11px] italic leading-relaxed text-[#4B5852]/75">
                        Estratto dall'intervista 'Visione e qualità di vita' – Primocanale
                      </p>
                      <a
                        href="https://www.primocanale.it/sanit%C3%A0/67274-visione-e-qualita-di-vita-intervista-professore-traverso.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${v.testid}-source`}
                        className="mt-1.5 inline-flex items-center gap-1 self-start text-[11px] font-medium text-[#2E6B50] underline decoration-[#2E6B50]/40 underline-offset-2 transition-colors duration-300 hover:text-[#0D3327] hover:decoration-[#0D3327]"
                      >
                        Guarda l'intervista completa su Primocanale →
                      </a>
                      <button
                        type="button"
                        onClick={() => setCurrent(v)}
                        data-testid={`${v.testid}-action`}
                        className="mt-5 inline-flex items-center gap-1.5 self-start text-xs font-semibold uppercase tracking-[0.14em] text-[#174C3A] transition-colors duration-300 hover:text-[#0D3327]"
                      >
                        Guarda il video <Play size={13} />
                      </button>
                    </div>
                  </article>
                </Reveal>
              ))}

            {filteredItems.map((item, i) => {
              const Icon = ICONS[item.categoria];
              return (
                <Reveal key={item.testid} delay={i * 0.06}>
                  <article
                    data-testid={item.testid}
                    className="group flex h-full flex-col overflow-hidden rounded-lg border border-[#D5E2DB] bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                  >
                    {item.thumb ? (
                      <img
                        src={item.thumb}
                        alt={item.titolo}
                        loading="lazy"
                        className="aspect-video w-full object-cover"
                      />
                    ) : (
                      <div className="flex aspect-video items-center justify-center bg-[#E8F1EC] transition-colors duration-300 group-hover:bg-[#dcebe2]">
                        <Icon size={36} className="text-[#174C3A]/60" strokeWidth={1.4} />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col p-6">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#2E6B50]">
                        {item.categoriaLabel}
                      </p>
                      <h3 className="mt-3 line-clamp-3 font-serif text-lg font-semibold leading-snug text-[#0D3327]">
                        {item.titolo}
                      </h3>
                      <p className="mt-2 flex-1 text-xs text-[#4B5852]">Fonte: {item.fonte}</p>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${item.testid}-action`}
                        className="mt-5 inline-flex items-center gap-1.5 self-start text-xs font-semibold uppercase tracking-[0.14em] text-[#174C3A] transition-colors duration-300 hover:text-[#0D3327]"
                      >
                        {item.azione} <ArrowUpRight size={14} />
                      </a>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          {isEmpty && (
            <p data-testid="media-empty-state" className="mt-12 text-center text-sm text-[#4B5852]">
              Nessun contenuto disponibile in questa categoria. Nuovi contenuti saranno pubblicati prossimamente.
            </p>
          )}
        </div>
      </section>

      {current && (
        <div
          data-testid="video-player-modal"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A281E]/92 p-4 backdrop-blur-sm"
          onClick={() => setCurrent(null)}
        >
          <div
            className="relative w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center px-5 py-4 pr-16 sm:px-6">
              <p
                data-testid="video-player-title"
                className="line-clamp-2 font-serif text-base font-semibold leading-snug text-[#1A221E] sm:text-lg"
              >
                {current.titolo}
              </p>
            </div>
            <button
              type="button"
              data-testid="video-player-close"
              onClick={() => setCurrent(null)}
              aria-label="Chiudi il video"
              className="absolute right-3 top-3 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-[#0D3327]/90 text-white shadow-md transition-colors duration-300 hover:bg-[#174C3A]"
            >
              <X size={18} />
            </button>
            <video
              key={current.src}
              data-testid="video-player"
              poster={current.thumb}
              controls
              autoPlay
              preload="auto"
              onCanPlay={(e) => {
                const p = e.currentTarget.play();
                if (p && p.catch) p.catch(() => {});
              }}
              className="aspect-video w-full bg-black"
            >
              <source src={current.src} type="video/mp4" />
              <source src={current.src.replace(".mp4", ".webm")} type="video/webm" />
            </video>
          </div>
        </div>
      )}
    </div>
  );
}
